import { STAFF_TYPE_IDS } from '@/constants/orderTypes'
import { TAB_INDEX } from '@/modules/dispatcher/constants/tabs'
import { parseDateRange } from '@/utils/date'

/**
 * Поля по умолчанию для заказа
 */
const ORDER_DEFAULTS = {
    id: null,
    num: '',
    created_date: null,
    delivered_date: null,
    is_favorite: false,
    is_invoice_printed: false,
    fiscal_printed: false,
    fiscal_required: false,
    is_ticket_printed: false,
    payments: [],
    courier: {
        name: null,
        phone: null,
    },
    address: {
        settlement: null,
        street: '',
        house: '',
        intercom: null,
        flat: null,
        floor: null,
        entrance: null,
        raw_address: '',
    },
    division: {
        name: '',
    },
    status: {
        stage: 0,
        name: '',
        id: '',
        color: '#6c757d',
    },
    order_type: {
        id: '',
        name: '',
        active: null,
    },
    time_type: null,
    route: null,
}

/**
 * Нормализация платежей
 */
const normalizePayments = payments => {
    if (!Array.isArray(payments)) return []

    return payments.map(p => ({
        id: p.id ?? p.payment_id ?? null,
        status: p.status ?? p.status_id ?? '',
        payment_type_id: p.payment_type_id ?? '',
        type: p.type
            ? {
                  name: p.type.name ?? '',
                  fiscal_required:
                      p.type.fiscal_required ?? p.type.fiscalRequired ?? false,
              }
            : null,
        price: p.price ?? p.amount ?? 0,
        final_price: p.final_price ?? p.amount ?? 0,
        change_from: p.change_from ?? p.changeFrom ?? 0,
    }))
}

/**
 * Нормализация курьера
 */
const normalizeCourier = courier => {
    if (!courier) return { ...ORDER_DEFAULTS.courier }

    return {
        id: courier.id ?? courier.courier_id ?? null,
        name: courier.name ?? courier.full_name ?? ORDER_DEFAULTS.courier.name,
    }
}

/**
 * Нормализация адреса
 */
const normalizeAddress = address => {
    if (!address) return { ...ORDER_DEFAULTS.address }

    return {
        settlement:
            address.settlement ??
            address.city ??
            ORDER_DEFAULTS.address.settlement,
        street: address.street ?? ORDER_DEFAULTS.address.street,
        house:
            address.house ?? address.building ?? ORDER_DEFAULTS.address.house,
        intercom: address.intercom ?? ORDER_DEFAULTS.address.intercom,
        flat: address.flat ?? address.apartment ?? ORDER_DEFAULTS.address.flat,
        floor: address.floor ?? ORDER_DEFAULTS.address.floor,
        entrance: address.entrance ?? ORDER_DEFAULTS.address.entrance,
        raw_address:
            address.raw_address ??
            address.full ??
            address.address ??
            ORDER_DEFAULTS.address.raw_address,
    }
}

/**
 * Нормализация подразделения
 */
const normalizeDivision = (division, divisionId) => {
    if (!division && !divisionId) return { ...ORDER_DEFAULTS.division }

    return {
        id: division?.id ?? divisionId ?? null,
        name: division?.name ?? division?.title ?? ORDER_DEFAULTS.division.name,
    }
}

/**
 * Нормализация статуса
 */
const normalizeStatus = status => {
    if (!status) return { ...ORDER_DEFAULTS.status }

    return {
        name: status.name ?? '',
        id: status.id ?? status.status_id ?? '',
    }
}

/**
 * Нормализация маршрута заказа (поле order.route в API).
 * Сохраняем при ADD_OR_UPDATE_ORDER, чтобы CDC order_updated не затирал
 * № маршрута в реестре заказов (EDS-5104).
 */
const normalizeOrderRoute = route => {
    if (!route) return null

    return {
        id: route.id ?? null,
        route_number: route.route_number ?? null,
    }
}

/**
 * Нормализация типа заказа
 */
const normalizeOrderType = orderType => {
    if (!orderType) return { ...ORDER_DEFAULTS.order_type }

    return {
        id: orderType.id ?? orderType.type_id ?? '',
        name: orderType.name ?? '',
    }
}

/**
 * Нормализует заказ из API в структуру приложения
 * @param {Object} apiOrder - заказ с бэкенда
 * @returns {Object} - нормализованный заказ
 */
export const normalizeOrder = apiOrder => {
    if (!apiOrder) {
        console.warn('normalizeOrder: apiOrder is null/undefined')
        return { ...ORDER_DEFAULTS }
    }

    return {
        // === Прямые поля ===
        id: apiOrder.id ?? ORDER_DEFAULTS.id,
        num: apiOrder.num ?? apiOrder.orderNumber ?? ORDER_DEFAULTS.num,
        created_date: apiOrder.created_date ?? ORDER_DEFAULTS.created_date,
        delivered_date:
            apiOrder.delivered_date ?? ORDER_DEFAULTS.delivered_date,
        is_favorite: apiOrder.is_favorite ?? ORDER_DEFAULTS.is_favorite,
        is_invoice_printed:
            apiOrder.is_invoice_printed ?? ORDER_DEFAULTS.is_invoice_printed,
        fiscal_printed:
            apiOrder.fiscal_printed ?? ORDER_DEFAULTS.fiscal_printed,
        fiscal_required:
            apiOrder.fiscal_required ?? ORDER_DEFAULTS.fiscal_required,
        time_type: apiOrder.time_type ?? ORDER_DEFAULTS.time_type,
        is_ticket_printed:
            apiOrder.is_ticket_printed ?? ORDER_DEFAULTS.is_ticket_printed,

        // === Вложенные объекты ===
        payments: normalizePayments(apiOrder.payments),
        courier: normalizeCourier(apiOrder.courier),
        address: normalizeAddress(apiOrder.address),
        division: normalizeDivision(apiOrder.division, apiOrder.division_id),
        status: normalizeStatus(apiOrder.status),
        order_type: normalizeOrderType(apiOrder.order_type),
        route: normalizeOrderRoute(apiOrder.route),
    }
}

export const isOrderMatchingTab = (order, currentTabIndex) => {
    const tabIndex = Number(currentTabIndex ?? TAB_INDEX.ORDERS)
    const isStaffOrder = STAFF_TYPE_IDS.includes(order.order_type?.id)

    if (tabIndex === TAB_INDEX.STAFF) return isStaffOrder
    if (isStaffOrder) return false

    switch (tabIndex) {
        case TAB_INDEX.IN_ROUTE:
            return !!order.route
        case TAB_INDEX.NOT_IN_ROUTE:
            return !order.route
        case TAB_INDEX.FAVORITES:
            return order.is_favorite === true
        case TAB_INDEX.PRINTED:
            return order.is_invoice_printed === true
        default:
            return true
    }
}

const normalizeSearchValue = value =>
    String(value ?? '')
        .trim()
        .toLowerCase()

const isEmptyFilter = value =>
    value === null || value === undefined || value === '' || value === 'all'

const isDateMatchingFilter = (date, filterDate) => {
    if (isEmptyFilter(filterDate)) return true
    if (!date) return false

    const orderDate = new Date(date)
    const { start, end } = parseDateRange(filterDate)

    if (!start || !end || Number.isNaN(orderDate.getTime())) return false

    return (
        orderDate >= start.startOf('day').toDate() &&
        orderDate <= end.endOf('day').toDate()
    )
}

const isStringMatchingFilter = (value, filterValue) => {
    if (isEmptyFilter(filterValue)) return true

    return normalizeSearchValue(value).includes(
        normalizeSearchValue(filterValue)
    )
}

const getOrderAddress = order =>
    [
        order.address?.raw_address,
        order.address?.settlement,
        order.address?.street,
        order.address?.house,
        order.address?.flat,
    ]
        .filter(Boolean)
        .join(' ')

const getOrderPrice = order =>
    order.payments?.reduce(
        (sum, payment) => sum + (Number(payment.final_price) || 0),
        0
    ) || 0

const isPriceMatchingFilter = (order, filters) => {
    const min = filters.price_min
    const max = filters.price_max

    if (isEmptyFilter(min) && isEmptyFilter(max)) return true

    const price = getOrderPrice(order)
    const minPrice = Number(min)
    const maxPrice = Number(max)

    if (!Number.isNaN(minPrice) && price < minPrice) return false
    if (!Number.isNaN(maxPrice) && price > maxPrice) return false

    return true
}

const isStatusesMatchingFilter = (order, statuses) => {
    if (!Array.isArray(statuses) || statuses.length === 0) return true
    if (statuses.includes('all')) return true

    return statuses.some(status => String(status) === String(order.status?.id))
}

const isCourierMatchingFilter = (order, courierId) => {
    if (isEmptyFilter(courierId)) return true

    return String(order.courier?.id ?? '') === String(courierId)
}

const isDivisionMatchingFilter = (order, divisionId) => {
    if (isEmptyFilter(divisionId)) return true

    return String(order.division?.id ?? '') === String(divisionId)
}

export const isOrderMatchingListContext = (order, context = {}) => {
    const filters = context.filters || {}

    if (!isOrderMatchingTab(order, context.tabIndex)) return false
    if (!isDivisionMatchingFilter(order, context.divisionId)) return false
    if (!isStatusesMatchingFilter(order, filters.statuses)) return false
    if (!isStringMatchingFilter(order.num, filters.num)) return false
    if (!isStringMatchingFilter(getOrderAddress(order), filters.address)) {
        return false
    }
    if (!isPriceMatchingFilter(order, filters)) return false
    if (!isCourierMatchingFilter(order, filters.courier_id)) return false
    if (!isDateMatchingFilter(order.delivered_date, filters.delivered_date)) {
        return false
    }
    if (!isDateMatchingFilter(order.created_date, filters.created_date)) {
        return false
    }

    return true
}

export const findOrderInRoutes = (routes, orderId) => {
    const targetRoute = routes.find(route =>
        route.orders?.some(routeOrder => routeOrder?.id === orderId)
    )
    if (!targetRoute) return null

    const orderIndex = targetRoute.orders.findIndex(
        routeOrder => routeOrder?.id === orderId
    )
    if (orderIndex === -1) return null

    return {
        route: targetRoute,
        orderIndex,
    }
}
